/**
 * EnemyLibrary — the in-game "THREAT ARCHIVE" overlay.
 *
 * 2026-threat-archive-console (Task-001/002) — a full rewrite. Build 81
 * (TestFlight, 16 Pro Max) shipped the D-326/D-328 grid→modal model with six
 * mechanism-level defects (vh/vw mixed with design-px in the scaled stage,
 * unmeasured control regions, sub-floor text, sub-floor touch targets, a
 * shared unclipped glow, raw-EN flavour) — see
 * `specs/2026-threat-archive-console/spec.md`. Direction A of the owner-picked
 * mockup (`docs/threat_archive_redesign_2026-08.html`, section RICHTUNG A)
 * collapses the two-level grid→modal model: **the archive opens AS the
 * dossier** — a diegetic full-screen analysis console. Exactly one threat is
 * ever on screen; a persistent 19-slot INDEX RAIL at the bottom (discovered =
 * lit glyph chip, undiscovered = quiet noise slot) both replaces the grid AND
 * is the switcher — tapping a chip re-renders the SAME dossier surface
 * in place (no close/reopen, no second overlay level).
 *
 * The five mockup construction rules (R1-R5) are structural, not decorative:
 *   R1 — every length in this file's CSS is design-px or a container
 *        percentage; vh/vw never appear (grep-tested,
 *        `tests/ui/EnemyLibrary.test.ts`'s R1 guard).
 *   R2 — the header and the rail are fixed, non-overlapping absolute regions;
 *        nothing floats over content (no negative offsets / margin-top:auto).
 *   R3 — the specimen glow/rings/holo bloom live ONLY inside `.elc-well`,
 *        which is `overflow:hidden`; every text node in the dossier is a
 *        sibling of `.elc-well`, never a descendant of it.
 *   R4 — every text floor is the mockup's own pre-multiplied number: function
 *        text >= 23 design-px, numerics >= 30, touch targets >= 96 — all
 *        comfortably clear the 16-Pro-Max-scale (0.4889) floors (>=11 CSS-px
 *        text, >=44 CSS-px touch) even before any `--chrome-scale` boost.
 *        Deliberately literal px (not `roleFontSize()`/`UI_TYPE`): those
 *        roles are what the grid's ~16px/~12px card text used to read, and
 *        16*0.4889=7.8 / 12*0.4889=5.9 are exactly the two numbers Befund #3
 *        names — the SAME scale that regressed once cannot be reused here.
 *   R5 — FENRIR's flavour resolves through i18n (`enemyCardModel.revealTaunts`,
 *        Task-002) in its own reserved `.elc-flav` region.
 *
 * Discovery: reads `EnemyReveal.isDiscovered` — the SAME run-scoped
 * `revealedTypes` set the in-run reveal card already maintains; the archive
 * never invents a second source of truth. Every `show()` tears down and
 * rebuilds from `isDiscovered` as it reads RIGHT NOW (unchanged verifier-fixed
 * contract from Task-012/AC-16) — a mid-run "discover more, reopen" archive
 * stays current, and a fresh run's archive is fully re-locked.
 *
 * Opens on (direction A order): the last-viewed entry (sticky across opens
 * within this instance, re-validated against the fresh discovered set so a
 * cross-run reset can never leak a stale dossier) → else the first discovered
 * entry in roster order → else the undiscovered-NOISE state (no name/stat
 * leak, mirrors the old locked-tile redaction).
 *
 * Perf: the constructor does zero card/holo/DOM work (lazy, unchanged
 * contract). The rail's 19 slots are each a single STATIC `EnemyHolo` frame
 * (D-328's own already-shipped "mount + `drawStatic()` once" idiom — proven
 * safe for many-at-once) built straight from `configToRevealData`, never the
 * heavier `buildEnemyCardContent` (which also derives ability/flavour text
 * and burns a `pickTaunt()` random draw per call — wasted work for a rail
 * chip). Exactly ONE holo is ever animated: the current dossier's, via
 * `EnemyHolo.start()` (already `isReducedMotion()`-gated — reduced motion
 * draws one static frame, no RAF is ever scheduled). The CSS ambient
 * glow/ring/hint animations carry their own `@media (prefers-reduced-motion:
 * reduce)` freeze, mirroring the mockup's own switch.
 *
 * Determinism: render/DOM-only, no sim state or sim RNG read or written.
 * `pickTaunt()`'s `Math.random()` is pre-existing render-only flavour choice
 * (never a sim quantity) — unchanged by this rewrite.
 */
import type { EnemyConfig } from "../entities/types.ts";
import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { PANEL, ELEMENT_COLORS } from "../rendering/designTokens.ts";
import { rarityMaterial, type RarityName } from "../core/palette.ts";
import { UI_FONT_STACK, UI_DURATION, UI_EASE } from "./uiTokens.ts";
import { announce, makeKeyboardActivatable } from "./a11y.ts";
import type { TradingCardContent, TradingCardRarity } from "./TradingCard.ts";
import { EnemyHolo } from "./EnemyHolo.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import {
  configToRevealData,
  buildEnemyCardContent,
  deriveEnemyRarity,
  classCode,
  type EnemyRevealData,
} from "./enemyCardModel.ts";

export interface EnemyLibraryOptions {
  /** The full roster (id → config), read straight from `enemies.json`. */
  enemies: Record<string, EnemyConfig>;
  /** Has this type (the config's display `name`) been revealed this run?
   *  Wired to `EnemyReveal.isDiscovered` in production. */
  isDiscovered: (typeName: string) => boolean;
  /**
   * A light haptic tick whenever the VISIBLE dossier changes to a newly-
   * shown discovered entry (the archive opening on one, or a rail tap
   * switching to another) — the console's continuation of D-328's "hero
   * opens" tick now that every switch is that same moment. Wired to
   * `nativeShell.triggerLibraryHeroHaptic` (`__NATIVE_SHELL__`-gated) in
   * production; a true no-op on web/desktop. Optional so tests never need it.
   * Name kept for caller compatibility (`main.ts` wires this field).
   */
  onHeroOpenHaptic?: (() => void) | undefined;
}

interface RosterEntry {
  id: string;
  cfg: EnemyConfig;
}

/** One readout stat row's live element refs (INTEGRITY/VELOCITY/BOUNTY). */
interface StatRow {
  k: HTMLElement;
  v: HTMLElement;
  fill: HTMLElement;
}

/** One matchup row's live element refs (WEAKNESS/RESIST). */
interface MatchRow {
  k: HTMLElement;
  chipHost: HTMLElement;
}

/** Direction A's stat-gauge normalization band (mockup "Trash/Elite-Band"):
 *  INTEGRITY/VELOCITY/BOUNTY gauges read against the real roster's own
 *  non-boss ceiling (siphon hp=700, splitling/hunter_killer speed=105, siphon
 *  reward=50 — `public/data/enemies.json`, verified 2026-08-04), so a trash
 *  type reads honestly low and boss/final_boss simply clip the bar at 100%
 *  (an intentional "off the chart" read, not a bug — `.elc-stat-gauge` is
 *  `overflow:hidden`). */
const STAT_GAUGE_MAX: readonly [number, number, number] = [700, 105, 50];

// ─── D-341 — ring carousel geometry. Named here rather than inlined so the
//     drag maths, the CSS card width and the layout all read the SAME numbers
//     (the owner's "Magic Numbers in Layout-Konstanten auslagern").
/** Card width as a percentage of the overlay — narrower than the screen, which
 *  is what lets the neighbours show. Mirrors `.elc-card { width: 56% }`. */
// Measured, not guessed: at 56% the shipped readout (58px values, 27px labels)
// overflowed its panels in a real browser — and shrinking that type would drop
// it under the 11 CSS-px floor after the stage transform, which the charter
// forbids. Widening the card is the allowed lever; the neighbours still show.
const RING_CARD_PCT = 62;
/** Slot pitch as a percentage of ONE CARD's width. <100 overlaps the cards
 *  slightly, so a neighbour is already visible before it starts moving. */
const RING_STEP_PCT = 66;
/** How far from focus a card is still drawn. Bounds the live DOM and, with it,
 *  the number of preview holos that can exist at once. */
const RING_VISIBLE = 2.6;

/** Mirrors `TradingCard.ts`'s own private `RARITY_MATERIAL` (a tiny 5-entry
 *  table) — duplicated here rather than exported, per the "leave
 *  TradingCard.ts untouched unless genuinely required" constraint (the
 *  console builds its own readout and never imports `TradingCard`). No new
 *  hex: colour still resolves through the central `RARITY` material. */
const RARITY_TIER_MATERIAL: Record<TradingCardRarity, RarityName> = {
  basic: "common",
  rare: "rare",
  epic: "epic",
  legendary: "legendary",
  secret: "epic",
};

export class EnemyLibrary {
  readonly el: HTMLDivElement;
  private readonly opts: EnemyLibraryOptions;
  private visible_ = false;
  private onKeydown: ((e: KeyboardEvent) => void) | null = null;
  private onClose: (() => void) | null = null;

  /** The full roster in `enemies.json` order — recomputed fresh every
   *  `show()` (cheap; no `isDiscovered` calls). */
  private roster: RosterEntry[] = [];
  /** Discovered ids in roster order — recomputed fresh every `show()`
   *  (the verifier-fixed "never a stale cache" contract). */
  private discoveredOrder: string[] = [];
  /** The id whose dossier is currently rendered; `null` = the noise state. */
  private currentId: string | null = null;
  /** True once `renderDossier` has run at least once since the last
   *  `teardown()` — lets `renderDossier(null)` on a fresh build still render
   *  (a bare `id === this.currentId` guard would wrongly no-op when both are
   *  `null` on first paint). */
  private currentRendered = false;
  /** Sticky "last viewed" id across opens (direction A's default) —
   *  re-validated against the fresh discovered set on every `show()`, so a
   *  cross-run reset can never leak a no-longer-discovered dossier. */
  private lastViewedId: string | null = null;

  // Cached refs into the live dossier, valid between build() and teardown().
  // (The header's progress label/bar are write-once per rebuild() — no cached
  // ref needed, unlike the dossier fields below, which `renderDossier()`
  // mutates repeatedly as the current entry switches.)
  private wellEl: HTMLElement | null = null;
  private wellArt: HTMLElement | null = null;
  private wellNoise: HTMLElement | null = null;
  private nameEl: HTMLElement | null = null;
  private subEl: HTMLElement | null = null;
  private pipsEl: HTMLElement | null = null;
  private statRows: StatRow[] = [];
  private matchRows: MatchRow[] = [];
  private abilityBlock: HTMLElement | null = null;
  private abilityT: HTMLElement | null = null;
  private abilityD: HTMLElement | null = null;
  // D-340: no flavour fields. FENRIR's taunt was removed from the dossier —
  // the console is an instrument, and its voice belongs to the reveal beat
  // (`EnemyReveal` → `TradingCard.tc-flavour`), where it plays once per enemy
  // instead of on every open. The i18n keys stay live and rendered there.

  /** Known (discovered) rail chip elements, keyed by id — the switcher. */
  // ─── D-341 — the endless ring carousel ────────────────────────────────────
  /** The 19 slot cards (one per roster entry, in roster order — the SAME order
   *  as the rail, so chip N and card N can never disagree). */
  private cards: HTMLElement[] = [];
  /** Continuous ring position in card units. Deliberately NOT clamped and NOT
   *  reduced mod 19: it may drift to 21.4 or -3.2 and each card is drawn at its
   *  NEAREST representative around the ring (`ringDelta`). That is what makes
   *  the loop seamless — there is no seam to jump over, so no index reset is
   *  ever visible (the failure the PreRunScreen carousel has: its `setFocus`
   *  hard-clamps and its wrapping arrow key visibly rewinds the whole strip). */
  private pos = 0;
  private carRaf = 0;
  /** Static preview holos of the neighbour cards — bounded, see `mountPeeks`. */
  private peekHolos = new Map<number, EnemyHolo>();
  private dragBound = false;

  private railKnownEls = new Map<string, HTMLElement>();
  /** Every rail-chip holo (all static) — torn down as a batch. */
  private railHolos: EnemyHolo[] = [];
  /** The ONE live/animated holo (the current dossier's specimen). */
  private specimenHolo: EnemyHolo | null = null;

  constructor(parent: HTMLElement, opts: EnemyLibraryOptions) {
    this.opts = opts;
    this.el = document.createElement("div");
    this.el.id = "enemy-library";
    this.el.setAttribute("role", "dialog");
    this.el.setAttribute("aria-modal", "true");
    this.el.setAttribute("aria-label", t("library.title", undefined, "THREAT ARCHIVE"));
    // The overlay swallows its own clicks so it never races the gear dropdown's
    // document-level click-away (the same pattern `GearMenu.menu` uses).
    this.el.addEventListener("click", (e) => e.stopPropagation());
    parent.appendChild(this.el);
    EnemyLibrary.ensureStyles();
  }

  get visible(): boolean {
    return this.visible_;
  }

  /** Open the archive. Never builds at construction (lazy — unchanged perf
   *  contract); every `show()` tears down and rebuilds fresh from
   *  `isDiscovered` (Task-012/AC-16's verifier fix, carried forward), then
   *  renders straight onto the dossier per direction A (AC-1) — there is no
   *  intermediate grid/list level to build or skip. */
  show(onClose?: () => void): void {
    this.onClose = onClose ?? null;
    this.rebuild();
    this.visible_ = true;
    this.el.classList.add("el-visible");
    announce(t("library.opened", undefined, "Threat archive opened."));
    // AC-1 — a single overlay level: Escape always closes the ARCHIVE (there
    // is no separate hero/modal level to unwind first). Arrow keys step
    // between discovered dossiers, mirroring the rail's own tap-to-switch.
    // Registered on `document` in the CAPTURE phase (mirrors `LegalOverlay`'s
    // idiom) and only while the archive is open, so it structurally fires
    // before any bubble-phase listener — including GearMenu's own document
    // Escape listener — regardless of focus location.
    if (!this.onKeydown) {
      this.onKeydown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          this.hide();
          return;
        }
        if (e.key === "ArrowRight") {
          e.stopPropagation();
          this.step(1);
          return;
        }
        if (e.key === "ArrowLeft") {
          e.stopPropagation();
          this.step(-1);
        }
      };
      document.addEventListener("keydown", this.onKeydown, true);
    }
    this.el.querySelector<HTMLElement>(".elc-hdr-close")?.focus();
  }

  hide(): void {
    if (!this.visible_) return;
    this.visible_ = false;
    this.el.classList.remove("el-visible");
    if (this.onKeydown) {
      document.removeEventListener("keydown", this.onKeydown, true);
      this.onKeydown = null;
    }
    // Stop paying for the one live/animated holo the instant the console is
    // no longer visible (mirrors the old hero's close-time teardown).
    this.teardown();
    this.onClose?.();
  }

  /** Tear down any previous dossier (DOM + holos), rebuild the roster +
   *  discovery set fresh, then render the direction-A entry point. */
  private rebuild(): void {
    this.teardown();
    this.roster = Object.entries(this.opts.enemies).map(([id, cfg]) => ({ id, cfg }));
    this.discoveredOrder = this.roster
      .filter((r) => this.opts.isDiscovered(r.cfg.name))
      .map((r) => r.id);
    this.buildHeader();
    this.buildSpread();
    this.buildRail();
    this.drawRailHolos();
    this.renderDossier(this.pickInitialId());
    this.syncRingToCurrent();
  }

  /** Direction A's opening choice: last-viewed (if still discovered) → first
   *  discovered in roster order → `null` (the noise state). */
  private pickInitialId(): string | null {
    if (this.lastViewedId && this.discoveredOrder.includes(this.lastViewedId)) {
      return this.lastViewedId;
    }
    return this.discoveredOrder[0] ?? null;
  }

  /** Tear down the live dossier (holos + the header/spread/rail DOM). Safe to
   *  call when nothing is built yet. */
  private teardown(): void {
    this.specimenHolo?.destroy();
    this.specimenHolo = null;
    for (const holo of this.railHolos) holo.destroy();
    this.railHolos = [];
    this.railKnownEls.clear();
    if (this.carRaf) {
      cancelAnimationFrame(this.carRaf);
      this.carRaf = 0;
    }
    for (const holo of this.peekHolos.values()) holo.destroy();
    this.peekHolos.clear();
    this.el.querySelector(".elc-hdr")?.remove();
    this.el.querySelector(".elc-track")?.remove();
    this.el.querySelector(".elc-spread")?.remove();
    this.el.querySelector(".elc-rail")?.remove();
    for (const hint of this.el.querySelectorAll(".elc-hint")) hint.remove();
    this.wellEl = null;
    this.wellArt = null;
    this.wellNoise = null;
    this.nameEl = null;
    this.subEl = null;
    this.pipsEl = null;
    this.statRows = [];
    this.matchRows = [];
    this.abilityBlock = null;
    this.abilityT = null;
    this.abilityD = null;
    this.cards = [];
    this.pos = 0;
    this.focusIdx = 0;
    this.dragging = false;
    this.currentId = null;
    this.currentRendered = false;
  }

  // ─── R2 — the fixed header region (title · progress · close) ────────────────

  private buildHeader(): void {
    const hdr = document.createElement("div");
    hdr.className = "elc-hdr";

    const title = document.createElement("div");
    title.className = "elc-hdr-title";
    title.textContent = `◈ ${t("library.title", undefined, "THREAT ARCHIVE")}`;

    const prog = document.createElement("div");
    prog.className = "elc-hdr-prog";
    const lbl = document.createElement("span");
    lbl.className = "elc-hdr-prog-lbl";
    const bar = document.createElement("span");
    bar.className = "elc-hdr-prog-bar";
    const fill = document.createElement("i");
    bar.appendChild(fill);
    prog.append(lbl, bar);

    const known = this.discoveredOrder.length;
    const total = this.roster.length;
    lbl.textContent = t("library.count", { known, total }, `${known}/${total} KNOWN`);
    fill.style.width = `${Math.max(4, Math.round((known / Math.max(1, total)) * 100))}%`;

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "elc-hdr-close";
    closeBtn.textContent = t("library.close", undefined, "✕ CLOSE");
    closeBtn.addEventListener("click", () => this.hide());

    hdr.append(title, prog, closeBtn);
    this.el.appendChild(hdr);
  }

  // ─── R2/R3 — the dossier: specimen (left, clipped well) + readout (right) ───

  private buildSpread(): void {
    const spread = document.createElement("div");
    spread.className = "elc-spread";

    // ── left — the specimen in its volumetric, CLIPPED light well (R3) ──
    const specimen = document.createElement("div");
    specimen.className = "elc-specimen";

    const well = document.createElement("div");
    well.className = "elc-well";
    const glow = document.createElement("div");
    glow.className = "elc-well-glow";
    glow.setAttribute("aria-hidden", "true");
    const ring1 = document.createElement("div");
    ring1.className = "elc-well-ring elc-well-ring--r1";
    ring1.setAttribute("aria-hidden", "true");
    const ring2 = document.createElement("div");
    ring2.className = "elc-well-ring elc-well-ring--r2";
    ring2.setAttribute("aria-hidden", "true");
    const wellArt = document.createElement("div");
    wellArt.className = "elc-well-art";
    const wellNoise = document.createElement("div");
    wellNoise.className = "elc-well-noise";
    wellNoise.setAttribute("aria-hidden", "true");
    wellNoise.style.display = "none";
    const noiseQ = document.createElement("span");
    noiseQ.className = "elc-well-noise-q";
    noiseQ.textContent = "?";
    wellNoise.appendChild(noiseQ);
    well.append(glow, ring1, ring2, wellArt, wellNoise);
    this.wellEl = well;
    this.wellArt = wellArt;
    this.wellNoise = wellNoise;

    const name = document.createElement("div");
    name.className = "elc-name";
    const sub = document.createElement("div");
    sub.className = "elc-sub";
    const pips = document.createElement("div");
    pips.className = "elc-pips";
    pips.setAttribute("aria-hidden", "true");
    this.nameEl = name;
    this.subEl = sub;
    this.pipsEl = pips;

    specimen.append(well, name, sub, pips);

    // ── right — the readout: stats, matchups, ability, flavour ──
    const readout = document.createElement("div");
    readout.className = "elc-readout";

    const statrow = document.createElement("div");
    statrow.className = "elc-statrow";
    this.statRows = [];
    for (let i = 0; i < 3; i++) {
      const stat = document.createElement("div");
      stat.className = "elc-stat";
      const k = document.createElement("div");
      k.className = "elc-stat-k";
      const v = document.createElement("div");
      v.className = "elc-stat-v";
      const gauge = document.createElement("div");
      gauge.className = "elc-stat-gauge";
      const fill = document.createElement("i");
      gauge.appendChild(fill);
      stat.append(k, v, gauge);
      statrow.appendChild(stat);
      this.statRows.push({ k, v, fill });
    }

    const matchrow = document.createElement("div");
    matchrow.className = "elc-matchrow";
    this.matchRows = [];
    for (let i = 0; i < 2; i++) {
      const match = document.createElement("div");
      match.className = "elc-match";
      const k = document.createElement("span");
      k.className = "elc-match-k";
      const chipHost = document.createElement("span");
      chipHost.className = "elc-chip-host";
      match.append(k, chipHost);
      matchrow.appendChild(match);
      this.matchRows.push({ k, chipHost });
    }

    const ability = document.createElement("div");
    ability.className = "elc-ability";
    const abilityT = document.createElement("div");
    abilityT.className = "elc-ability-t";
    const abilityD = document.createElement("div");
    abilityD.className = "elc-ability-d";
    ability.append(abilityT, abilityD);
    this.abilityBlock = ability;
    this.abilityT = abilityT;
    this.abilityD = abilityD;

    // D-340: the ability panel is the readout's LAST block and absorbs the
    // column's remaining height (`.elc-ability { flex:1 }`), so the right
    // column ends on the same baseline as the specimen column instead of
    // stopping short where the removed flavour block used to hang.
    readout.append(statrow, matchrow, ability);

    spread.append(specimen, readout);
    this.buildTrack(spread);
  }

  // ─── D-341 — the ring: track, slot cards, and the ONE live dossier ─────────

  /**
   * Build the sliding track: one slot card per roster entry, in roster order.
   *
   * Only ONE full dossier exists — the `spread` built above — and it is MOVED
   * into whichever slot currently holds focus. Every other slot shows a cheap
   * `.elc-peek` preview (a static holo + name + class, or the locked plate).
   * That is the whole memory argument: a phone pays for one animated holo and
   * at most four static preview holos, never nineteen dossiers.
   *
   * It is also what keeps this change honest against the existing suite: there
   * is still exactly one `.elc-spread`, one `.elc-name`, one `.elc-well-art`
   * in the document, so `populateDossier` and every selector that already
   * exists keep meaning what they meant.
   */
  private buildTrack(spread: HTMLElement): void {
    const track = document.createElement("div");
    track.className = "elc-track";
    this.cards = this.roster.map((_, i) => {
      const card = document.createElement("div");
      card.className = "elc-card";
      card.dataset["idx"] = String(i);
      for (const c of ["tl", "tr", "bl", "br"]) {
        const cnr = document.createElement("i");
        cnr.className = `elc-cnr elc-cnr--${c}`;
        cnr.setAttribute("aria-hidden", "true");
        card.appendChild(cnr);
      }
      track.appendChild(card);
      return card;
    });
    // The spread starts in slot 0; `focusRing` moves it the moment the entry
    // point is chosen (rebuild → renderDossier → syncRingToCurrent).
    this.cards[0]?.appendChild(spread);
    this.el.appendChild(track);
    this.bindRingDrag();
  }

  /** Ring distance from `pos` to slot `i`, always the SHORTER way around: the
   *  result lands in [-n/2, +n/2), so slot 2 seen from 17 is +4, never -15.
   *  One function serves the layout, the arrow keys and the chip jump — the
   *  "keine drei getrennten Indeximplementierungen" the owner asked for. */
  private ringDelta(i: number, from = this.pos): number {
    const n = this.cards.length || 1;
    return (((i - from) % n) + n + n / 2) % n - n / 2;
  }

  /**
   * The live slot. A FIELD, not derived from `pos`.
   *
   * Deriving it from the track position looked tidy and was wrong twice over:
   * `pos` only advances while animation frames run, so with RAF throttled (a
   * backgrounded tab, or any headless run) two arrow presses in a row both
   * computed the same "next" slot and the archive got stuck one card in. The
   * position is where the track IS; this is what the player has SELECTED, and
   * only a drag lets the former drive the latter.
   */
  private focusIdx = 0;
  /** True only between pointerdown and pointerup — see `layoutRing`. */
  private dragging = false;

  /**
   * Place every card at its nearest ring position and scale/fade it by that
   * distance. Called on every drag move and every glide frame, so the cards
   * track the finger continuously instead of snapping between fixed states.
   *
   * Measured layout only — a safe no-op in jsdom, where every offset is 0.
   */
  private layoutRing(): void {
    const n = this.cards.length;
    if (!n) return;
    for (let i = 0; i < n; i++) {
      const card = this.cards[i]!;
      const d = this.ringDelta(i);
      const ad = Math.abs(d);
      if (ad > RING_VISIBLE) {
        card.style.display = "none";
        continue;
      }
      card.style.display = "";
      const near = Math.min(ad, 1);
      const far = Math.min(Math.max(ad - 1, 0), 1.5);
      const scale = 1 - 0.26 * near - 0.06 * far;
      const opacity = 1 - 0.44 * near - 0.16 * far;
      card.style.transform =
        `translateX(${(d * RING_STEP_PCT).toFixed(3)}%)` +
        ` translateY(${(near * 2.6).toFixed(2)}%) scale(${scale.toFixed(3)})`;
      card.style.opacity = opacity.toFixed(3);
      card.style.zIndex = String(100 - Math.round(ad * 10));
      card.classList.toggle("elc-card--sel", ad < 0.5);
    }
    // Only a DRAG lets the track drive the selection, and it hands over at the
    // crossing point — mid-gesture, where the outgoing and incoming cards are
    // the same size and brightness, so the one content change is spent at the
    // least visible instant. A glide never does this: its destination was
    // already selected before it started.
    if (this.dragging) this.applyFocus(((Math.round(this.pos) % n) + n) % n);
    this.mountPeeks();
  }

  /**
   * Keep the live dossier on the nearest slot and previews on the rest.
   *
   * The hand-over happens at the CROSSING point (`ad < 0.5`) — mid-drag, while
   * the outgoing and incoming cards are at the same distance and therefore the
   * same size and brightness — and never when a glide finishes. That ordering
   * is the point: the owner's rail is "kein abruptes Umspringen nach Abschluss
   * der Animation", so the one content change is spent at the least visible
   * instant instead of on the settled, full-brightness card.
   */
  private mountPeeks(): void {
    const focus = this.focusIdx;
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]!;
      const inWindow = Math.abs(this.ringDelta(i)) <= RING_VISIBLE;
      const wantPeek = inWindow && i !== focus;
      const peek = card.querySelector(".elc-peek");
      if (wantPeek && !peek) {
        card.appendChild(this.buildPeek(i));
        this.peekHolos.get(i)?.drawStatic();   // attached first — see buildPeek
      }
      if (!wantPeek && peek) {
        peek.remove();
        this.peekHolos.get(i)?.destroy();
        this.peekHolos.delete(i);
      }
    }
  }

  /**
   * A neighbour preview: deliberately LESS than the dossier (the owner's own
   * spec for the side cards) — silhouette, name, class. No stats, no matchup,
   * no ability. An undiscovered slot shows the locked plate and, per AC-6 of
   * the owner's brief, never its name.
   */
  private buildPeek(i: number): HTMLElement {
    const entry = this.roster[i]!;
    const peek = document.createElement("div");
    peek.className = "elc-peek";
    peek.setAttribute("aria-hidden", "true");
    const known = this.discoveredOrder.includes(entry.id);

    const art = document.createElement("div");
    art.className = "elc-peek-art";
    peek.appendChild(art);

    if (!known) {
      peek.classList.add("elc-peek--locked");
      const lock = document.createElement("div");
      lock.className = "elc-peek-lock";
      lock.textContent = "▮";
      art.appendChild(lock);
      const label = document.createElement("div");
      label.className = "elc-peek-name";
      label.textContent = t("library.encrypted", undefined, "ENCRYPTED");
      peek.appendChild(label);
      return peek;
    }

    const revealData = configToRevealData(entry.id, entry.cfg);
    const { content } = buildEnemyCardContent(revealData);
    // The rail's shipped idiom: a STATIC single frame, never `.start()`. Only
    // the focused dossier animates — the neighbours must not each cost a RAF.
    const holo = new EnemyHolo({
      shape: revealData.shape,
      colour: revealData.colour,
      tier: deriveEnemyRarity(revealData),
    });
    art.appendChild(holo.el);
    this.peekHolos.set(i, holo);
    // F-R4-1, and it very nearly happened again here: do NOT draw yet. This
    // subtree is still detached, and `EnemyHolo.draw()` reads clientWidth/Height
    // — 0x0 on a detached node — so it would bail and leave a BLANK glyph, the
    // exact regression the rail already paid for once. `mountPeeks` appends
    // first and draws after, mirroring `drawRailHolos()`.

    const name = document.createElement("div");
    name.className = "elc-peek-name";
    name.textContent = content.name;
    const sub = document.createElement("div");
    sub.className = "elc-peek-sub";
    sub.textContent = `${content.subtitle} · ${content.classBadge}`;
    peek.append(name, sub);
    peek.style.setProperty("--elc-peek-id", content.colour);
    return peek;
  }

  /** Glide the ring to an absolute (unwrapped) position. Reduced motion and
   *  headless settle instantly — the destination is identical either way, so
   *  the reduced-motion path loses the travel, never the result. */
  private glideRing(to: number): void {
    // The selection is bound to the DESTINATION here, at the one choke point
    // every glide passes through, and synchronously — before a single frame
    // runs. Binding it anywhere else is how the shipped bug happened: the drag
    // release cleared `dragging` and only THEN started the throw, so
    // focus-follows-position (which is gated on `dragging`) never learned where
    // the momentum actually landed. The ring settled one card further than the
    // dossier and the rail, every single swipe. Doing it here makes that class
    // of mistake unrepresentable rather than fixing the one call site.
    const n = this.cards.length;
    if (n) this.applyFocus((((Math.round(to) % n) + n) % n));
    if (this.carRaf) {
      cancelAnimationFrame(this.carRaf);
      this.carRaf = 0;
    }
    const from = this.pos;
    const dist = to - from;
    if (
      isReducedMotion() ||
      typeof requestAnimationFrame === "undefined" ||
      Math.abs(dist) < 0.001
    ) {
      this.pos = to;
      this.layoutRing();
      return;
    }
    const dur = Math.min(620, Math.max(240, Math.abs(dist) * 190));
    const t0 = performance.now();
    const ease = (x: number): number => 1 - Math.pow(1 - x, 3);
    const frame = (now: number): void => {
      const p = Math.min(1, (now - t0) / dur);
      this.pos = from + dist * ease(p);
      this.layoutRing();
      this.carRaf = p < 1 ? requestAnimationFrame(frame) : 0;
    };
    this.carRaf = requestAnimationFrame(frame);
  }

  /**
   * Make slot `i` the live one: move the single dossier into it and render.
   *
   * Deliberately SEPARATE from the glide. Selection is a logical fact and must
   * not wait on animation frames — an early version tied the two together and
   * a chip tap then did nothing at all whenever RAF did not run (a backgrounded
   * tab, reduced motion, the test env). It also keeps the owner's AC 7 true by
   * construction: the active chip and the centre card are set in one place, so
   * they cannot drift apart.
   */
  private applyFocus(i: number): void {
    const spread = this.el.querySelector<HTMLElement>(".elc-spread");
    const card = this.cards[i];
    if (!spread || !card || spread.parentElement === card) return;
    this.focusIdx = i;
    card.appendChild(spread);
    this.renderDossier(this.roster[i]?.id ?? null);
  }

  /** Move focus to an exact slot the SHORT way around the ring. The rail chips,
   *  `step()` and any jump-to-card all funnel through here: the selection lands
   *  at once, the travel is what gets animated. */
  private focusRing(i: number): void {
    this.applyFocus(i);
    this.glideRing(this.pos + this.ringDelta(i));
  }

  /** Point the ring at whatever `currentId` is, without animating — used by the
   *  entry point so opening on a late `lastViewedId` starts centred. */
  private syncRingToCurrent(): void {
    const i = this.roster.findIndex((r) => r.id === this.currentId);
    if (i < 0) return;
    this.pos = i;
    this.focusIdx = i;
    this.layoutRing();
  }

  /**
   * Pointer drag = swipe (touch), drag (mouse/simulator) and pen in one path.
   * `touch-action: pan-y` in CSS keeps vertical scrolling while we own X.
   * A drag that starts on the rail or the close button is left alone so those
   * controls keep working.
   */
  private bindRingDrag(): void {
    if (this.dragBound) return;
    this.dragBound = true;
    let active = false;
    let x0 = 0;
    let p0 = 0;
    let lastX = 0;
    let lastT = 0;
    let vel = 0;
    const stepPx = (): number => {
      const w = this.el.clientWidth || 1;
      return (w * RING_CARD_PCT * RING_STEP_PCT) / 10000 || 1;
    };
    this.el.addEventListener("pointerdown", (e) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(".elc-rail, .elc-hdr")) return;
      active = this.dragging = true;
      x0 = lastX = e.clientX;
      p0 = this.pos;
      vel = 0;
      lastT = performance.now();
      if (this.carRaf) {
        cancelAnimationFrame(this.carRaf);
        this.carRaf = 0;
      }
    });
    this.el.addEventListener("pointermove", (e) => {
      if (!active) return;
      this.pos = p0 - (e.clientX - x0) / stepPx();
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 8) {
        vel = (e.clientX - lastX) / dt;
        lastX = e.clientX;
        lastT = now;
      }
      this.layoutRing();
    });
    const end = (): void => {
      if (!active) return;
      active = this.dragging = false;
      // Momentum: a fast flick carries past the neighbour, a slow drag settles
      // on the nearest card. Rounding AFTER the throw keeps both on the ring.
      const thrown = this.pos - (vel * 0.13 * (this.el.clientWidth || 0)) / stepPx();
      this.glideRing(Math.round(thrown));
    };
    this.el.addEventListener("pointerup", end);
    this.el.addEventListener("pointercancel", end);
  }

  // ─── R2 — the persistent 19-slot index rail (the switcher + the "grid") ─────

  private buildRail(): void {
    const rail = document.createElement("div");
    rail.className = "elc-rail";
    const hintL = document.createElement("span");
    hintL.className = "elc-hint elc-hint--l";
    hintL.setAttribute("aria-hidden", "true");
    hintL.textContent = "‹";
    const hintR = document.createElement("span");
    hintR.className = "elc-hint elc-hint--r";
    hintR.setAttribute("aria-hidden", "true");
    hintR.textContent = "›";
    this.el.append(hintL, hintR);

    const discovered = new Set(this.discoveredOrder);
    for (const { id, cfg } of this.roster) {
      rail.appendChild(
        discovered.has(id) ? this.buildKnownChip(id, cfg) : this.buildUnknownChip(),
      );
    }
    this.el.appendChild(rail);
  }

  /**
   * Draw every rail chip's single static holo frame — called once `buildRail()`
   * (above) has appended the WHOLE rail (every chip, each already carrying its
   * canvas) into `this.el`, which has been part of `parent`'s document since
   * construction (`rebuild()`/`teardown()` only ever tear down and rebuild
   * `this.el`'s CHILDREN, never `this.el` itself).
   *
   * Round-4 verifier F-R4-1: `buildKnownChip` used to call `drawStatic()`
   * itself, on the chip BEFORE it was appended anywhere — `EnemyHolo.draw()`
   * sizes the canvas from `host.clientWidth/Height` (`EnemyHolo.ts:148`),
   * which is unconditionally 0 on a detached node, so it bailed before
   * painting a single pixel (a live browser measured 0 of 45,000 painted on
   * every rail glyph). Drawing here instead — AFTER the chip is attached —
   * makes that same read force the browser to flush any pending layout
   * synchronously (the standard "forced reflow" a layout-property read always
   * triggers): nothing between `.elc-idx-glyph` and `document` is
   * `display:none` (`#enemy-library` itself is only `opacity:0`, which still
   * generates full layout), so it already measures the real 56x56 in the very
   * same synchronous tick — no different, mechanically, from why the well's
   * holo (`renderDossier`, unchanged) has always painted correctly.
   *
   * The one-shot bounded (never a loop) rAF retry below exists only for the
   * case that synchronous read still can't cover — `host` still reading 0x0
   * right here. It is only ever SCHEDULED for a holo whose first read was
   * already 0x0 (so it can never double-draw one that already painted), and
   * the `railHolos` membership check at fire time keeps a stale retry from an
   * earlier `show()` cycle from touching a holo a later `hide()`/reopen has
   * already torn down and replaced. Gated on `!isReducedMotion()` — AC-7's
   * own "reduced motion costs no RAF" contract (mirrors `EnemyHolo.start()`'s
   * own gate) applies here too: the synchronous draw above already ships the
   * single static frame reduced motion is owed, so the retry (a real-browser
   * safety net this codebase's own forced-reflow read should rarely need —
   * see above) is not worth an RAF the accessibility signal promises zero of.
   */
  private drawRailHolos(): void {
    for (const holo of this.railHolos) {
      holo.drawStatic();
      const host = holo.el.parentElement;
      if (!isReducedMotion() && host && (host.clientWidth === 0 || host.clientHeight === 0)) {
        requestAnimationFrame(() => {
          if (this.railHolos.includes(holo)) holo.drawStatic();
        });
      }
    }
  }

  private buildKnownChip(id: string, cfg: EnemyConfig): HTMLElement {
    const revealData = configToRevealData(id, cfg);
    const rarity = deriveEnemyRarity(revealData);

    const chip = document.createElement("div");
    chip.className = "elc-idx elc-idx--known";
    chip.dataset["enemyId"] = id;
    chip.setAttribute("aria-label", revealData.name);

    const glyph = document.createElement("div");
    glyph.className = "elc-idx-glyph";
    glyph.setAttribute("aria-hidden", "true");
    const code = document.createElement("div");
    code.className = "elc-idx-code";
    code.textContent = classCode(revealData);
    chip.append(glyph, code);

    // D-341 — a chip tap no longer hard-switches the dossier; it drives the
    // ring to that slot the short way round, and the arrival re-renders. Same
    // path as a swipe, so the chip and the card can never disagree.
    makeKeyboardActivatable(chip, () => {
      const i = this.roster.findIndex((r) => r.id === id);
      if (i >= 0) this.focusRing(i);
    });

    // D-328's shipped idiom, continued: a STATIC single-frame holo (never
    // `.start()`) — the rail is small (<=19, always on screen or one scroll
    // away, never virtualised) and every discovered chip needs the SAME
    // identity read the old grid gave. The canvas mounts here, but this chip
    // is still a DETACHED subtree (not yet appended to `rail`, let alone
    // `this.el`) — `drawStatic()` deliberately does NOT run here any more
    // (F-R4-1, round-4 verifier): `buildRail()`'s `drawRailHolos()` pass
    // draws every rail holo in one go, once the whole rail has actually been
    // attached (see its doc comment for the mechanism).
    const holo = new EnemyHolo({
      shape: revealData.shape,
      colour: revealData.colour,
      tier: rarity,
    });
    glyph.appendChild(holo.el);
    this.railHolos.push(holo);
    this.railKnownEls.set(id, chip);

    return chip;
  }

  private buildUnknownChip(): HTMLElement {
    const chip = document.createElement("div");
    chip.className = "elc-idx elc-idx--unk";
    // Quiet noise, per direction A — NOT tappable (no click/keyboard wiring,
    // no role="button", no tabindex): a locked signature isn't a target.
    chip.setAttribute("aria-label", t("library.unknown", undefined, "SIGNAL UNKNOWN"));
    const q = document.createElement("span");
    q.className = "elc-idx-q";
    q.setAttribute("aria-hidden", "true");
    q.textContent = "?";
    chip.appendChild(q);
    return chip;
  }

  /** Step to the previous/next DISCOVERED dossier (arrow keys) — wraps both
   *  ends, mirrors tapping the neighbouring rail chip. No-op with 0-1
   *  discovered or while showing the noise state. */
  private step(dir: 1 | -1): void {
    // D-341 — the arrow keys now drive the SAME ring as the swipe and the
    // chips: one slot per press over all 19 roster entries, undiscovered ones
    // included (they render as the locked plate, never as data). Before the
    // ring this walked `discoveredOrder` and skipped the gaps, which meant the
    // keyboard and the rail disagreed about what "next" was.
    const n = this.cards.length;
    if (n < 2) return;
    // Step from the SELECTED slot, not from the live track position — mid-glide
    // `pos` is a drifting float and `pos + dir` would round somewhere else.
    this.focusRing((((this.focusIdx + dir) % n) + n) % n);
  }

  // ─── AC-1 — the single-level switch: re-render the SAME dossier surface ────

  /** Render one dossier (or the noise state for `null`) into the ALREADY-
   *  BUILT header/spread/rail — never tears down or rebuilds the overlay
   *  itself. This is the whole point of AC-1: a rail tap (or an arrow-key
   *  step) calls straight into this, so there is no close/reopen cycle for
   *  switching between threats. */
  private renderDossier(id: string | null): void {
    if (this.currentRendered && id === this.currentId) return;
    this.currentRendered = true;
    this.currentId = id;
    if (id) this.lastViewedId = id;

    for (const [cid, chipEl] of this.railKnownEls) {
      const isCur = cid === id;
      chipEl.classList.toggle("elc-idx--cur", isCur);
      if (isCur) {
        chipEl.setAttribute("aria-current", "true");
        // F5 (review-round-2) — opening on a late lastViewedId or arrow-stepping
        // past the fold otherwise leaves the highlighted chip off-screen; the
        // rail IS the switcher (AC-1), so its current chip must stay reachable.
        // Feature-detected: jsdom (this file's own test env) does not implement
        // scrollIntoView at all — an unguarded call throws there.
        if (typeof chipEl.scrollIntoView === "function") {
          chipEl.scrollIntoView({ inline: "nearest", block: "nearest" });
        }
      } else {
        chipEl.removeAttribute("aria-current");
      }
    }

    this.specimenHolo?.destroy();
    this.specimenHolo = null;

    const cfg = id ? this.opts.enemies[id] : undefined;
    // D-341 — the ring carries ALL 19 slots (owner, 2026-08-06), so this is now
    // reachable with an UNDISCOVERED id: a swipe, an arrow key or a mis-wired
    // chip can all land here. Before the ring, only `discoveredOrder` could
    // reach this method, and `cfg` alone decided. `cfg` exists for every enemy
    // in the roster — discovered or not — so gating on it would happily print
    // an unmet threat's name, HP and weakness. The discovery check belongs HERE,
    // at the one place that renders, not at each of the three call sites.
    if (!id || !cfg || !this.discoveredOrder.includes(id)) {
      this.renderNoise();
      return;
    }

    const revealData = configToRevealData(id, cfg);
    const { content } = buildEnemyCardContent(revealData);
    this.populateDossier(revealData, content);

    this.wellEl?.classList.remove("elc-well--noise");
    this.wellNoise!.style.display = "none";
    this.wellArt!.style.display = "";
    const holo = new EnemyHolo({
      shape: revealData.shape,
      colour: revealData.colour,
      tier: deriveEnemyRarity(revealData),
    });
    this.wellArt!.replaceChildren(holo.el);
    void holo.el.offsetWidth;
    holo.start(); // isReducedMotion()-gated internally — reduced draws once, no RAF
    this.specimenHolo = holo;

    announce(content.name);
    this.opts.onHeroOpenHaptic?.();
  }

  private populateDossier(d: EnemyRevealData, content: TradingCardContent): void {
    this.nameEl!.textContent = content.name;
    this.subEl!.textContent = `${content.subtitle} · ${content.classBadge}`;

    const tierColour = rarityMaterial(RARITY_TIER_MATERIAL[content.rarity]).mid;
    this.el.style.setProperty("--elc-id", content.colour);
    this.el.style.setProperty("--elc-tier", tierColour);
    this.renderPips(content.pips ?? 1, tierColour);

    const stats = content.stats ?? [];
    const gauges: readonly number[] = [d.hp, d.speed, d.reward];
    this.statRows.forEach((row, i) => {
      const s = stats[i];
      row.k.textContent = s?.k ?? "";
      row.v.textContent = s?.v ?? "";
      row.v.classList.toggle("elc-stat-v--hot", s?.tone === "hot");
      row.v.classList.toggle("elc-stat-v--gold", s?.tone === "gold");
      const raw = gauges[i] ?? 0;
      const max = STAT_GAUGE_MAX[i] ?? 100;
      const pct = Math.min(100, Math.max(4, Math.round((raw / max) * 100)));
      row.fill.style.width = `${pct}%`;
      // Owner, 2026-08-08 — the full bar used to say the same thing for Siphon
      // (700), Core Breaker (1500) and AXIOM-77 Core (6500): three enemies, a
      // ninefold spread, one identical bar. A value that EXCEEDS the scale now
      // reads gold with a slow specular sweep — "the chart cannot hold this",
      // instead of pretending they are equal.
      //
      // Strictly `>`, never `>=`: Siphon sits EXACTLY on 700 and its full bar is
      // honest, so it stays normal. Speed's ceiling (105) is the roster's real
      // maximum, so nothing can exceed it and the treatment never fires there —
      // which is correct, "fastest" is not "off the chart".
      //
      // The GLINT carries the meaning, not the colour: KOPFGELD's value text is
      // already amber for every enemy (`tone: "gold"` in enemyCardModel), so a
      // merely gold bar would read as decoration there. Nothing else in the
      // archive moves like this.
      row.fill.classList.toggle("elc-stat-fill--overscale", raw > max);
    });

    this.matchRows[0]!.k.textContent = t("card.label.weakness", undefined, "WEAKNESS");
    this.matchRows[0]!.chipHost.replaceChildren(
      this.buildChip(d.weakness, content.weakness ?? "", "◆"),
    );
    this.matchRows[1]!.k.textContent = t("card.label.resist", undefined, "RESIST");
    this.matchRows[1]!.chipHost.replaceChildren(
      this.buildChip(d.resistance, content.resistance ?? "", "◇"),
    );

    this.abilityBlock!.style.display = "";
    this.abilityBlock!.classList.remove("elc-ability--redacted");
    this.abilityT!.textContent = content.ability?.title ?? "";
    this.abilityD!.textContent = content.ability?.desc ?? "";
    // D-340: `content.flavour` is deliberately NOT read here. The taunt still
    // exists on the shared card model and still renders on the reveal card;
    // the dossier just no longer repeats it.
  }

  /** An element chip (matchup row): coloured from the RAW element key (never
   *  the already-localized label, which `elementLabel()` upper-cases and
   *  cannot be mapped back to a colour) via the central `ELEMENT_COLORS`; the
   *  `.elc-chip--none` dashed/neutral treatment when there's no matchup. */
  private buildChip(
    rawEl: string | null | undefined,
    label: string,
    glyph: "◆" | "◇",
  ): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "elc-chip";
    if (!rawEl) {
      chip.classList.add("elc-chip--none");
      chip.textContent = label;
      return chip;
    }
    const colour = (ELEMENT_COLORS as Record<string, string>)[rawEl] ?? COLORS.TEXT_SECONDARY;
    chip.style.setProperty("--elc-c", colour);
    chip.textContent = `${glyph} ${label}`;
    return chip;
  }

  private renderPips(n: number, tierColour: string): void {
    this.pipsEl!.replaceChildren();
    const total = 4;
    for (let i = 0; i < total; i++) {
      const pip = document.createElement("span");
      pip.className = i < n ? "elc-pip" : "elc-pip elc-pip--off";
      if (i < n) pip.style.setProperty("--elc-c", tierColour);
      this.pipsEl!.appendChild(pip);
    }
  }

  /** The undiscovered-noise dossier (AC-1's third opening fallback) — the
   *  console's own redaction, mirroring the old locked tile's guarantee: no
   *  name/stat/art leak for a type nothing has discovered yet. */
  private renderNoise(): void {
    this.nameEl!.textContent = t("library.unknown", undefined, "SIGNAL UNKNOWN");
    this.subEl!.textContent = "";
    this.pipsEl!.replaceChildren();

    this.el.style.setProperty("--elc-id", COLORS.SENTINEL_GREEN_DIM);
    this.el.style.setProperty("--elc-tier", COLORS.TEXT_SECONDARY);

    for (const row of this.statRows) {
      row.v.textContent = "—";
      row.v.classList.remove("elc-stat-v--hot", "elc-stat-v--gold");
      row.fill.classList.remove("elc-stat-fill--overscale");
      row.fill.style.width = "0%";
    }
    const none = t("card.label.none", undefined, "— none —");
    this.matchRows[0]!.k.textContent = t("card.label.weakness", undefined, "WEAKNESS");
    this.matchRows[0]!.chipHost.replaceChildren(this.buildChip(null, none, "◆"));
    this.matchRows[1]!.k.textContent = t("card.label.resist", undefined, "RESIST");
    this.matchRows[1]!.chipHost.replaceChildren(this.buildChip(null, none, "◇"));

    // D-340: the empty dossier no longer collapses. Before, ability AND
    // flavour were both hidden, so the readout column ended after the matchup
    // row and left ~40 % of the screen as dead space — the Build-81 "flach und
    // gestaucht" complaint surviving in the first-contact case, which is
    // precisely the screen a new player sees first. It now states its own
    // emptiness in the console's voice and keeps the column's full height.
    this.abilityBlock!.style.display = "";
    this.abilityBlock!.classList.add("elc-ability--redacted");
    this.abilityT!.textContent = t("library.redacted.title", undefined, "NO DOSSIER");
    this.abilityD!.textContent = t(
      "library.redacted.body",
      undefined,
      "Nothing has been recorded on this signal. Dossiers are written on first contact — hold the line and the archive fills itself.",
    );

    this.wellEl?.classList.add("elc-well--noise");
    this.wellArt!.style.display = "none";
    this.wellArt!.replaceChildren();
    this.wellNoise!.style.display = "";

    announce(t("library.unknown", undefined, "SIGNAL UNKNOWN"));
  }

  destroy(): void {
    if (this.onKeydown) {
      document.removeEventListener("keydown", this.onKeydown, true);
      this.onKeydown = null;
    }
    this.teardown();
    this.el.remove();
  }

  /**
   * review-round-2 fixes (kept here, OUTSIDE the CSS template literal below,
   * so the rationale costs zero production bytes — everything inside the
   * template literal ships verbatim in the bundle, comments included):
   *  - F1: `.elc-idx-glyph` had no `position`, so the canvas EnemyHolo mounts
   *    inside it (TradingCard.ts's `.tc-holo-canvas`, `inset:-18px`) landed
   *    against `.elc-idx` (the whole 120x126 chip) instead — off-centre,
   *    oversized, glow riding the code label. `.elc-well-art` happened to
   *    already be positioned, so it "worked" but was never archive-owned
   *    either. Both hosts now declare `position` and pin their own `canvas`
   *    rule explicitly (position + inset), and BOTH now share the well's
   *    -18px/+36px overscan (mirrors EnemyHolo's own OVERSCAN=18, duplicated
   *    the same way TradingCard.ts already duplicates it). Round-2 had
   *    instead given the glyph a tight `inset:0` fit, reasoning that nothing
   *    clips a rail chip and an overscanning canvas would ride onto
   *    `.elc-idx-code` below it — but art-feel-review round 3 found that
   *    tight fit inverts the chip's hierarchy BY ARITHMETIC, not taste:
   *    EnemyHolo sizes its composition from the HOST (`r = min(cw,ch) *
   *    0.27`, `EnemyHolo.ts:166`) and renders into a host+36 buffer
   *    (`:157-158`) whose widest layers (glow 2.7r, rays 2.9r) were built
   *    for the well's matched-overscan 1:1 display; downscaled ×0.609 into
   *    the tight 56px box, the silhouette lands at ~18.4 design px —
   *    smaller than the 26px `.elc-idx-code` label under it, and the one
   *    place the archive renders the same EnemyHolo component at a
   *    different scale than the well. Matched overscan puts the silhouette
   *    back at ~30.2 design px (> the label, as in the owner-picked mockup,
   *    and at unit parity with the well); the bleed concern is instead
   *    killed by `overflow:hidden` on `.elc-idx-glyph` itself — the glyph
   *    now clips its own overscan exactly as `.elc-well` already clips the
   *    well's, so nothing can ride onto the label.
   *  - F2: the phone-tier `.elc-hdr` override covered `padding-left` only;
   *    the close button is right-anchored and the modal's only touch exit,
   *    so it now gets the matching `padding-right` (mirrors HUD.ts's
   *    `.hud-controls` rule).
   *  - F3: `.elc-rail`'s chip gap was 8px design (3.9 CSS-px at the
   *    16-Pro-Max scale) — bumped to 18px to clear the >=8 CSS-px
   *    touch-target spacing canon; the rail already scrolls horizontally.
   *  - F5: `renderDossier` now scrolls the current rail chip into view
   *    (feature-detected — jsdom has no `scrollIntoView`), see below.
   */
  private static stylesInjected = false;
  private static ensureStyles(): void {
    if (EnemyLibrary.stylesInjected || document.getElementById("enemy-library-styles"))
      return;
    EnemyLibrary.stylesInjected = true;
    const G = COLORS.SENTINEL_GREEN;
    const GD = COLORS.SENTINEL_GREEN_DIM;
    const RS = COLORS.ORACLE_RED;
    // D-340: ORACLE_RED_DIM was only ever the flavour attribution's colour;
    // the taunt left the dossier, so the token left with it.
    const style = document.createElement("style");
    style.id = "enemy-library-styles";
    style.textContent = `
      /* ═══ R1 — every length below is design-px or a % of the immediate
         container. NO vh/vw ANYWHERE in this stylesheet (grep-tested) — the
         :685 defect class (viewport units racing design-px in the
         stage-transformed container) is structurally unbuildable here. ═══ */
      #enemy-library {
        position:absolute; inset:0; z-index:1500; overflow:hidden;
        /* D-340 — FULLY opaque, was rgba(...,0.97). Those 3 % let the paused
           HUD and the pause menu read through as LEGIBLE TEXT ("PUNKTE",
           "WELLE 5", "ZURÜCK", plus two ghost ‹ › arrows) behind the dossier,
           which is what made a finished screen read like an unfinished web
           overlay. A console either occludes or it isn't a console; partial
           transparency here bought atmosphere and paid in legible litter. */
        background:rgb(2,2,8);
        opacity:0; pointer-events:none;
        transition:opacity ${UI_DURATION.settle}ms ${UI_EASE.settle};
        font-family:${UI_FONT_STACK};
        --elc-id:${GD}; --elc-tier:${COLORS.TEXT_SECONDARY};
        --elc-id-core: color-mix(in srgb, var(--elc-id) 45%, white 55%);
      }
      #enemy-library.el-visible { opacity:1; pointer-events:all; }
      @media (prefers-reduced-motion: reduce) {
        #enemy-library { transition:none; }
        /* The sheen freezes mid-bar into a static highlight. The gold and the
           still specular both remain, so the "off the chart" reading survives
           without motion — reduced motion must cost the animation, never the
           information. */
        .elc-stat-fill--overscale::after {
          animation:none; transform:translateX(90%);
        }
      }

      /* ── R2 — the header: ONE fixed, measured region (104px). Title,
         progress and close each own their slot; nothing floats over content. */
      .elc-hdr {
        position:absolute; left:0; right:0; top:0; height:104px;
        display:flex; align-items:center; gap:36px;
        padding:0 44px 0 150px;
        border-bottom:1px solid color-mix(in srgb, ${G} 16%, transparent);
        background:linear-gradient(180deg, ${PANEL.BG_ELEVATED}88, transparent);
        z-index:40;
      }
      .elc-hdr-title {
        font-size:42px; color:${G}; letter-spacing:5px; white-space:nowrap;
        text-shadow:0 0 22px color-mix(in srgb, ${G} 45%, transparent);
      }
      .elc-hdr-prog { flex:1; min-width:0; display:flex; align-items:center; gap:18px; }
      .elc-hdr-prog-lbl {
        font-size:28px; color:${COLORS.TEXT_SECONDARY}; letter-spacing:2px; white-space:nowrap;
      }
      .elc-hdr-prog-bar {
        flex:1; max-width:520px; height:8px; border-radius:4px;
        background:color-mix(in srgb, ${G} 10%, transparent); overflow:hidden; display:block;
      }
      .elc-hdr-prog-bar i {
        display:block; height:100%; border-radius:4px; background:${G};
        box-shadow:0 0 14px color-mix(in srgb, ${G} 80%, transparent);
      }
      .elc-hdr-close {
        height:96px; min-width:230px; display:flex; align-items:center; justify-content:center;
        font-family:${UI_FONT_STACK}; font-size:30px; letter-spacing:3px;
        color:${COLORS.TEXT_SECONDARY}; background:color-mix(in srgb, ${PANEL.BG} 90%, transparent);
        border:1px solid ${GD}; border-radius:8px; cursor:pointer;
        transition:all ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      .elc-hdr-close:hover, .elc-hdr-close:focus-visible {
        color:white; border-color:${G}; background:color-mix(in srgb, ${G} 12%, transparent);
      }
      html.phone-tier #enemy-library .elc-hdr {
        padding-left: max(150px, var(--inset-left-design, 0px));
        padding-right: max(44px, var(--inset-right-design, 0px)); /* F2 */
      }

      /* ── D-341 — the ring track occupies R2's second fixed region; the cards
         inside it are positioned by TRANSFORM only, so DOM order stays roster
         order (matching the rail) while visual order follows the ring. */
      .elc-track {
        position:absolute; left:0; right:0; top:104px; bottom:214px; z-index:10;
        touch-action:pan-y;                 /* we own X, the page keeps Y */
      }
      .elc-card {
        position:absolute; top:0; height:100%; left:50%;
        width:${RING_CARD_PCT}%; margin-left:-${RING_CARD_PCT / 2}%;
        will-change:transform,opacity;
        /* The card body: a glass plate — a cool vertical gradient, a soft top
           light, and a fine rule grid. This is the "nicht steril" the owner
           asked for: the surface is never one flat fill. */
        background:
          radial-gradient(120% 80% at 50% 0%, color-mix(in srgb, ${G} 11%, transparent) 0%, transparent 60%),
          radial-gradient(150% 46% at 50% 4%, transparent 47%, color-mix(in srgb, ${G} 7%, transparent) 48%, transparent 50%),
          radial-gradient(180% 54% at 50% -6%, transparent 52%, color-mix(in srgb, ${COLORS.AMBER_WARN} 6%, transparent) 53%, transparent 55%),
          linear-gradient(180deg, ${PANEL.BG_ELEVATED} 0%, ${PANEL.BG} 55%, ${COLORS.BG_PRIMARY} 100%);
        border:1px solid color-mix(in srgb, ${G} 20%, transparent);
        border-radius:22px;
        box-shadow:0 0 0 1px rgba(0,0,0,0.44), 0 26px 70px -14px rgba(0,0,0,0.80);
        overflow:hidden;
      }
      /* The upper light edge — light doing the work, not another border. */
      .elc-card::before {
        content:''; position:absolute; top:0; left:8%; right:8%; height:1px; z-index:4;
        background:linear-gradient(90deg, transparent, ${G}, transparent); opacity:0.5;
      }
      /* Fine rules + a vignette. A STATIC grid, never a moving scanline: the
         photosensitivity charter rules out drifting high-contrast banding. */
      .elc-card::after {
        content:''; position:absolute; inset:0; pointer-events:none; z-index:1; opacity:0.5;
        background:
          linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px) 0 0 / 100% 14px,
          radial-gradient(90% 60% at 50% 42%, transparent 55%, rgba(0,0,0,0.27) 100%);
      }
      .elc-card--sel {
        border-color:color-mix(in srgb, ${COLORS.AMBER_WARN} 30%, transparent);
        box-shadow:0 0 0 1px rgba(0,0,0,0.44),
                   0 0 52px -16px color-mix(in srgb, ${G} 55%, transparent),
                   0 26px 70px -14px rgba(0,0,0,0.80);
      }
      /* Corner ticks — pure form, and only on the focused card so they read as
         "this one is live" instead of as chrome sprayed over everything. */
      .elc-card--sel .elc-cnr {
        position:absolute; width:34px; height:34px; z-index:5;
        border:1px solid color-mix(in srgb, ${COLORS.AMBER_WARN} 44%, transparent);
      }
      .elc-card .elc-cnr { display:none; }
      .elc-card--sel .elc-cnr { display:block; }
      .elc-cnr--tl { top:16px; left:16px; border-right:0; border-bottom:0; border-radius:8px 0 0 0; }
      .elc-cnr--tr { top:16px; right:16px; border-left:0; border-bottom:0; border-radius:0 8px 0 0; }
      .elc-cnr--bl { bottom:16px; left:16px; border-right:0; border-top:0; border-radius:0 0 0 8px; }
      .elc-cnr--br { bottom:16px; right:16px; border-left:0; border-top:0; border-radius:0 0 8px 0; }

      /* ── the dossier — now the CONTENT of whichever card holds focus. */
      .elc-spread {
        position:absolute; inset:0; display:flex; z-index:3;
        /* Measured: 26/30 pushed the readout 11px past its box and the column
           15px past the card. The card's own border + corner ticks already give
           the inset the padding was there to provide. */
        padding:14px 18px;
      }

      /* ── a neighbour preview: less than the dossier, by design. */
      .elc-peek {
        position:absolute; inset:0; z-index:3;
        display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px;
        padding:30px;
      }
      .elc-peek-art { width:260px; height:260px; position:relative; }
      .elc-peek-art canvas { width:100%; height:100%; display:block; }
      .elc-peek-art::after {
        content:''; position:absolute; inset:-14%; border-radius:50%; pointer-events:none;
        border:1px solid color-mix(in srgb, ${COLORS.AMBER_WARN} 22%, transparent);
        background:radial-gradient(circle,
          color-mix(in srgb, var(--elc-peek-id, ${GD}) 14%, transparent) 0%, transparent 70%);
      }
      .elc-peek-name {
        font-size:46px; color:${COLORS.TEXT_PRIMARY}; letter-spacing:1px; text-align:center;
        max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
      }
      .elc-peek-sub { font-size:24px; letter-spacing:3px; color:${COLORS.TEXT_SECONDARY}; text-align:center; }
      .elc-peek--locked .elc-peek-art {
        background:repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 10px, transparent 10px 20px);
        border-radius:14px; display:flex; align-items:center; justify-content:center;
      }
      .elc-peek--locked .elc-peek-art::after { border-color:transparent; background:none; }
      .elc-peek-lock { font-size:90px; color:${GD}; line-height:1; }
      .elc-peek--locked .elc-peek-name { font-size:30px; letter-spacing:5px; color:${COLORS.TEXT_SECONDARY}; }

      /* ── the specimen: the well (R3 — glow/rings/art CLIPPED here, and
         ONLY here) + the name/sub/pips, which are SIBLINGS of the well, never
         its descendants — a glow layer can never share an unclipped box with
         a text node by construction. */
      .elc-specimen {
        /* D-341: 43% was right against the FULL overlay width. Inside a card the
           readout is what runs out of room first (measured: the matchup row
           needed 400 of 329 design px), and the emblem is the one element that
           loses nothing by being narrower — so the width comes from here rather
           than from the type, which may not shrink past the 11 CSS-px floor. */
        position:relative; width:34%; min-width:0; flex:none;
        display:flex; flex-direction:column; align-items:center; justify-content:center;
        /* D-341: the old 110px left pad centred the specimen against the FULL
           overlay width. Inside a 56%-wide card it would push it off-centre. */
      }
      .elc-well {
        /* D-341: 440 was the full-overlay figure. Measured inside a card the
           specimen column came to 583 of 554 available design px, and the well
           is the only block there that is pure image — so it gives up the 60px,
           not the name or the class line. */
        position:relative; width:380px; height:380px; flex:none;
        overflow:hidden; /* R3 — the ONE clip boundary the specimen glow lives inside */
      }
      .elc-well-glow {
        position:absolute; inset:0;
        background:radial-gradient(circle, color-mix(in srgb, var(--elc-id) 34%, transparent) 0%, transparent 62%);
        animation:elcBreathe 5.2s ${UI_EASE.glide} infinite;
      }
      .elc-well-ring {
        position:absolute; border-radius:50%;
        border:1px solid color-mix(in srgb, var(--elc-id) 34%, transparent);
      }
      .elc-well-ring--r1 { inset:26px; animation:elcSpin 26s linear infinite; }
      .elc-well-ring--r2 { inset:78px; border-style:dashed; animation:elcSpin 44s linear infinite reverse; }
      .elc-well-art {
        position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
      }
      /* F1 — archive-owned mount (see ensureStyles' doc comment); overscan
         kept, .elc-well's overflow:hidden clips either choice the same. */
      .elc-well-art canvas {
        position:absolute; inset:-18px;
        width:calc(100% + 36px); height:calc(100% + 36px); pointer-events:none;
      }
      .elc-well-noise {
        position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
        background:repeating-linear-gradient(45deg, color-mix(in srgb, ${RS} 6%, transparent) 0 3px, transparent 3px 7px);
      }
      .elc-well-noise-q { font-size:120px; color:${GD}; opacity:0.5; }
      .elc-well--noise .elc-well-glow, .elc-well--noise .elc-well-ring { display:none; }
      @keyframes elcBreathe { 0%,100%{transform:scale(1);opacity:0.85} 50%{transform:scale(1.06);opacity:1} }
      @keyframes elcSpin { to { transform:rotate(360deg); } }

      .elc-name {
        margin-top:4px; font-size:58px; line-height:1.05; color:white; letter-spacing:3px;
        text-align:center; max-width:100%; overflow-wrap:break-word; word-break:normal;
        /* C2 (2026-08-08): was 22px — nearly double the shipped reveal card's
           12px (TradingCard's .tc-name), which is what made this line read soft
           against the pixel-mono beneath it. Matched to the card so one enemy
           name looks the same wherever it appears. */
        text-shadow:0 0 12px color-mix(in srgb, var(--elc-id) 72%, transparent), 0 2px 2px black;
      }
      .elc-sub {
        margin-top:8px; font-size:29px; letter-spacing:3px; color:var(--elc-id-core);
        text-align:center; max-width:100%; overflow-wrap:break-word; word-break:normal;
      }
      .elc-pips { display:flex; gap:9px; margin-top:12px; }
      .elc-pip { width:13px; height:13px; transform:rotate(45deg); background:var(--elc-c, ${G}); box-shadow:0 0 9px var(--elc-c, ${G}); }
      .elc-pip--off { background:transparent; border:1px solid rgba(255,255,255,0.16); box-shadow:none; }

      /* ── the readout — every stat/matchup/ability/flavour region is its
         own bounded box (R2); nothing overlaps another slot's text. */
      .elc-readout {
        position:relative; flex:1; min-width:0; padding:28px 56px 14px 26px;
        display:flex; flex-direction:column; gap:20px;
      }
      .elc-statrow { display:flex; gap:12px; }
      .elc-stat {
        /* D-341: 22px side padding + a 20px row gap left the German
           "INTEGRITÄT" 11 design px short inside a card. The 11px come out of
           the box, not out of the label — after the stage transform this type
           is already near the 11 CSS-px floor. */
        /* 2026-08-08: a plain flex:1 forced three EQUAL boxes, so the widest
           value decided nothing and simply clipped — "1500 HP" needed 244 design
           px inside a 192 px box, and both bosses lost their last glyph. This
           predates the gold treatment; it only became visible once the same rows
           started drawing attention to themselves.
           Growing from the content lets the HP box take what a four-digit value
           needs while the narrower two give it back. Nothing is shrunk — the
           58px value and the 27px label both stay exactly as shipped, which
           matters because this type already sits near the 11 CSS-px floor after
           the stage transform. */
        flex:1 1 auto; min-width:0; position:relative; border-radius:10px; padding:14px 14px;
        border:1px solid ${PANEL.BORDER};
        background:linear-gradient(180deg, ${PANEL.BG_ELEVATED}, ${PANEL.BG});
        box-shadow:inset 0 1px 0 color-mix(in srgb, ${G} 10%, transparent);
      }
      .elc-stat-k { font-size:27px; letter-spacing:2px; color:${COLORS.TEXT_SECONDARY}; white-space:nowrap; }
      .elc-stat-v { font-size:58px; line-height:1.02; margin-top:2px; color:${COLORS.TEXT_PRIMARY}; font-variant-numeric:tabular-nums; white-space:nowrap; }
      .elc-stat-v--hot { color:${RS}; }
      .elc-stat-v--gold { color:${COLORS.AMBER_WARN}; }
      .elc-stat-gauge { margin-top:10px; height:7px; border-radius:4px; background:rgba(255,255,255,0.06); overflow:hidden; }
      .elc-stat-gauge i {
        display:block; height:100%; border-radius:4px; position:relative; overflow:hidden;
        background:color-mix(in srgb, var(--elc-id) 78%, white 8%);
        box-shadow:0 0 10px color-mix(in srgb, var(--elc-id) 70%, transparent);
      }
      /* ── OVER-SCALE (owner, 2026-08-08): the value exceeds the gauge ceiling,
         so the bar stops claiming a proportion and states its own limit instead.
         Gold for the worth, a travelling specular for the meaning. Metal, not
         neon: two amber stops with a lighter crown, so it reads as a milled
         surface catching light rather than a glowing strip. */
      .elc-stat-fill--overscale {
        background:linear-gradient(
          180deg,
          color-mix(in srgb, ${COLORS.AMBER_WARN} 62%, white 26%) 0%,
          ${COLORS.AMBER_WARN} 46%,
          color-mix(in srgb, ${COLORS.AMBER_WARN} 74%, black 22%) 100%) !important;
        box-shadow:0 0 12px color-mix(in srgb, ${COLORS.AMBER_WARN} 66%, transparent),
                   inset 0 1px 0 rgba(255,255,255,0.42) !important;
      }
      /* The sweep: a narrow blade of light crossing once, then a long pause.
         Deliberately slow and infrequent — a bar that strobes would read cheap
         and would sit badly with the photosensitivity charter. Transform-only,
         so it composites on the GPU and costs no layout. */
      .elc-stat-fill--overscale::after {
        content:''; position:absolute; top:0; bottom:0; left:0; width:38%;
        background:linear-gradient(100deg,
          transparent 0%,
          rgba(255,255,255,0.10) 32%,
          rgba(255,255,255,0.72) 50%,
          rgba(255,255,255,0.10) 68%,
          transparent 100%);
        transform:translateX(-140%);
        animation:elcOverscaleSheen 4.2s ${UI_EASE.glide} infinite;
        pointer-events:none;
      }
      @keyframes elcOverscaleSheen {
        0%   { transform:translateX(-140%); }
        /* The blade crosses in the first third; the rest of the cycle is rest. */
        34%  { transform:translateX(320%); }
        100% { transform:translateX(320%); }
      }
      .elc-matchrow { display:flex; gap:12px; align-items:stretch; }
      .elc-match {
        /* D-341: label BESIDE value needed 390 of 363 design px inside a card
           and was the single constraint forcing the card wider — which is what
           squeezed the neighbours out of sight. Stacking label OVER value costs
           height (absorbed by the flex:1 ability block below) instead of width,
           and matches the stat boxes above, so the readout now reads as one
           family rather than two. */
        flex:1; min-width:0; display:flex; flex-direction:column;
        align-items:flex-start; justify-content:center; gap:6px;
        border-radius:10px; padding:12px 14px; border:1px solid ${PANEL.BORDER};
        background:linear-gradient(180deg, ${PANEL.BG_ELEVATED}, ${PANEL.BG});
      }
      .elc-match-k { font-size:26px; letter-spacing:2px; color:${COLORS.TEXT_SECONDARY}; white-space:nowrap; }
      .elc-chip {
        display:inline-flex; align-items:center; gap:10px; font-size:27px; letter-spacing:1px;
        padding:6px 16px; white-space:nowrap; border-radius:7px;
        border:1px solid color-mix(in srgb, var(--elc-c, ${COLORS.TEXT_SECONDARY}) 55%, transparent);
        color:var(--elc-c, ${COLORS.TEXT_SECONDARY});
        background:color-mix(in srgb, var(--elc-c, ${COLORS.TEXT_SECONDARY}) 12%, transparent);
        text-shadow:0 0 10px color-mix(in srgb, var(--elc-c, ${COLORS.TEXT_SECONDARY}) 55%, transparent);
      }
      .elc-chip--none { border-style:dashed; background:transparent; text-shadow:none; }
      .elc-ability {
        /* D-340 — absorbs the readout's remaining height so the right column
           lands on the specimen column's baseline. Before, the flavour block
           carried margin-top:auto and did this job; removing it without this
           would leave the hole one row further down instead of closing it. */
        flex:1; min-height:0;
        border-left:3px solid var(--elc-id); border-radius:0 10px 10px 0; padding:14px 22px;
        background:linear-gradient(90deg, color-mix(in srgb, var(--elc-id) 9%, ${PANEL.BG}), ${PANEL.BG});
      }
      .elc-ability-t { font-size:32px; letter-spacing:2px; color:var(--elc-id-core); }
      .elc-ability-d {
        font-size:28px; line-height:1.38; color:${COLORS.TEXT_PRIMARY}; margin-top:5px;
        max-width:58ch; overflow-wrap:break-word; word-break:normal;
      }
      /* D-340 — the redacted variant of the ability panel: same frame, no
         accent bar, muted text. The empty dossier keeps the column's shape
         instead of leaving a hole where a panel should be. */
      .elc-ability--redacted {
        border-left-color:color-mix(in srgb, ${GD} 45%, transparent);
        background:linear-gradient(90deg, color-mix(in srgb, ${GD} 5%, ${PANEL.BG}), ${PANEL.BG});
      }
      .elc-ability--redacted .elc-ability-t { color:${GD}; }
      .elc-ability--redacted .elc-ability-d { color:${COLORS.TEXT_SECONDARY}; }

      /* ── R2 — the persistent 19-slot index rail: the second fixed region
         AND the switcher (AC-1). Scrolls horizontally if content ever
         outgrows the band (worst case: every slot discovered). Gap 18px
         (F3) clears the >=8 CSS-px chip-spacing canon (was 3.9 at 8px). */
      .elc-rail {
        position:absolute; left:121px; right:40px; bottom:57px; height:132px;
        display:flex; gap:18px; align-items:flex-end; z-index:20;
        overflow-x:auto; overflow-y:hidden; -webkit-overflow-scrolling:touch;
        overscroll-behavior:contain;
      }
      html.phone-tier #enemy-library .elc-rail {
        left: max(121px, var(--inset-left-design, 0px));
        right: max(40px, var(--inset-right-design, 0px));
        bottom: max(57px, var(--inset-bottom-design, 0px));
      }
      .elc-idx {
        position:relative; height:126px; flex:none; border-radius:9px;
        border:1px solid ${PANEL.BORDER};
        background:linear-gradient(180deg, ${PANEL.BG_ELEVATED}, ${PANEL.BG});
        display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px;
        transition:transform ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      /* R4 — every INTERACTIVE rail target (known chips) is >= 96 design-px
         per side (>= 44 CSS-px effective at the 16-Pro-Max 0.4889 scale, was
         21.5 pre-fix); unknown/noise slots are deliberately NOT interactive
         (no click/keyboard wiring, no role/tabindex) so the floor doesn't
         apply to them (direction A: "leiser: Hierarchie", never a target). */
      .elc-idx--known { width:120px; cursor:pointer; }
      .elc-idx--known:hover, .elc-idx--known:focus-visible { transform:translateY(-6px); }
      /* F1/art-feel-round-3 — archive-owned mount, matched overscan (see
         ensureStyles' doc comment for the full before/after); overflow:hidden
         on the host clips the bleed, same as .elc-well does for the well art. */
      .elc-idx-glyph { position:relative; width:56px; height:56px; overflow:hidden; }
      .elc-idx-glyph canvas {
        position:absolute; inset:-18px;
        width:calc(100% + 36px); height:calc(100% + 36px); pointer-events:none;
      }
      .elc-idx-code { font-size:26px; letter-spacing:2px; color:${COLORS.TEXT_PRIMARY}; white-space:nowrap; }
      .elc-idx--cur {
        border-color:color-mix(in srgb, var(--elc-id) 70%, transparent);
        box-shadow:0 0 18px color-mix(in srgb, var(--elc-id) 40%, transparent), inset 0 0 12px color-mix(in srgb, var(--elc-id) 14%, transparent);
      }
      .elc-idx--cur::after {
        content:""; position:absolute; left:50%; top:-26px; width:1px; height:26px;
        background:linear-gradient(180deg, transparent, color-mix(in srgb, var(--elc-id) 80%, transparent));
      }
      .elc-idx--unk {
        width:64px; height:104px; opacity:0.5;
        background:repeating-linear-gradient(45deg, color-mix(in srgb, ${RS} 5%, transparent) 0 3px, transparent 3px 7px), ${PANEL.BG};
      }
      .elc-idx-q { font-size:34px; color:${GD}; }
      .elc-hint {
        position:absolute; top:44%; font-size:44px; color:color-mix(in srgb, ${G} 34%, transparent);
        z-index:15; animation:elcHint 2.8s ${UI_EASE.glide} infinite;
      }
      .elc-hint--l { left:132px; }
      .elc-hint--r { right:46px; }
      @keyframes elcHint { 0%,100%{opacity:0.28} 50%{opacity:0.6} }

      /* ── R5/AC-7 — reduced motion: static planes, identical information,
         no RAF. The one JS-driven RAF loop (the current dossier's specimen
         holo, EnemyHolo.start()) already self-gates via isReducedMotion();
         this freezes every CSS-driven ambient animation (the REAL, live OS
         prefers-reduced-motion query — independent of the JS signal). */
      @media (prefers-reduced-motion: reduce) {
        #enemy-library .elc-well-glow,
        #enemy-library .elc-well-ring,
        #enemy-library .elc-hint,
        #enemy-library .elc-idx {
          animation:none !important; transition:none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

/** Test/reset hook — mirrors `TradingCard`'s (`__resetTradingCardForTest`):
 *  drops the injected-style STATIC guard so a suite that removes
 *  `#enemy-library-styles` from `<head>` (the established per-test cleanup
 *  idiom) gets it re-injected on the next `new EnemyLibrary()` instead of
 *  the guard silently short-circuiting `ensureStyles()` for the rest of the
 *  file's run (the DOM-id half of the guard alone is not enough once the
 *  element has been removed at least once). */
export function __resetEnemyLibraryForTest(): void {
  EnemyLibrary["stylesInjected"] = false;
}
